import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Globe, ExternalLink, MoreVertical, Trash2, Edit3, Eye, EyeOff, Loader2, Calendar, Sparkles, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { showApiError } from '@/lib/errors';
import { wbSitesApi, wbPagesApi, type WbSite } from '@/lib/api/services/websiteBuilder';
import TemplateGallery from './TemplateGallery';
import { MultiPageGenerateModal } from '@/components/website-builder/MultiPageGenerateModal';

function formatDate(dateStr: string) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return d.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric',
    ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  });
}

const GRADIENTS = [
  'from-violet-500 to-purple-700',
  'from-blue-500 to-indigo-700',
  'from-emerald-500 to-teal-700',
  'from-orange-500 to-rose-600',
  'from-pink-500 to-fuchsia-700',
  'from-cyan-500 to-blue-600',
  'from-amber-500 to-orange-600',
  'from-green-500 to-emerald-700',
];

function siteGradient(name: string) {
  return GRADIENTS[(name?.charCodeAt(0) ?? 0) % GRADIENTS.length];
}

export default function WebsiteBuilderDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [sites, setSites] = useState<WbSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WbSite | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', slug: '', domain: '', description: '' });
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [multiPageTarget, setMultiPageTarget] = useState<WbSite | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => { loadSites(); }, []);

  async function loadSites() {
    setLoading(true);
    setLoadError(null);
    const res = await wbSitesApi.list();
    if (res.error) {
      setLoadError(res.error);
      setSites([]);
    } else {
      setSites((res.data as any)?.data ?? res.data ?? []);
    }
    setLoading(false);
  }

  function handleTemplateComplete(siteId: string, firstPageId: string) {
    setTemplateModalOpen(false);
    navigate(`/home/website-builder/sites/${siteId}/editor/${firstPageId}`);
  }

  function autoSlug(name: string) {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  async function handleCreate() {
    if (!form.name || !form.slug) return;
    setCreating(true);
    const res = await wbSitesApi.create({
      name: form.name,
      slug: form.slug,
      domain: form.domain || undefined,
      description: form.description || undefined,
    });
    setCreating(false);
    if (res.error) {
      showApiError(res, { fallback: { title: 'Error' } });
      return;
    }
    toast({ variant: "success", title: 'Site created!' });
    setCreateOpen(false);
    setForm({ name: '', slug: '', domain: '', description: '' });
    loadSites();
  }

  async function handleDelete(site: WbSite) {
    const res = await wbSitesApi.delete(site.id);
    if (res.error) {
      showApiError(res, { fallback: { title: 'Error' } });
      return;
    }
    toast({ variant: "success", title: 'Site deleted' });
    setDeleteTarget(null);
    loadSites();
  }

  async function handleTogglePublish(site: WbSite) {
    const res = await wbSitesApi.update(site.id, { is_published: !site.is_published });
    if (res.error) showApiError(res, { fallback: { title: 'Error' } });
    else loadSites();
  }

  async function openEditorForSite(site: WbSite) {
    const pagesRes = await wbPagesApi.list(site.id);
    if (pagesRes.error) {
      showApiError(pagesRes, { fallback: { title: 'Error' } });
      return;
    }

    const pages = ((pagesRes.data as any)?.data ?? pagesRes.data ?? []) as Array<{ id: string; slug?: string }>;
    const firstPage = pages.find((p) => p.slug === 'home') || pages[0];

    if (firstPage?.id) {
      navigate(`/home/website-builder/sites/${site.id}/editor/${firstPage.id}`);
      return;
    }

    const createRes = await wbPagesApi.create(site.id, { title: 'Home', slug: 'home' });
    if (createRes.error) {
      showApiError(createRes, { fallback: { title: 'Error' } });
      return;
    }

    const createdPage = ((createRes.data as any)?.data ?? createRes.data) as { id?: string };
    if (!createdPage?.id) {
      toast({ title: 'Error', description: 'Could not open editor for this site.', variant: 'destructive' });
      return;
    }
    navigate(`/home/website-builder/sites/${site.id}/editor/${createdPage.id}`);
  }

  const isEmptyState = !loading && !loadError && sites.length === 0;

  if (isEmptyState) {
    return (
      <>
        <div data-testid="wb-dashboard-empty-gallery">
          <TemplateGallery
            onComplete={handleTemplateComplete}
            onStartBlank={() => setCreateOpen(true)}
          />
        </div>

        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Create New Site</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Site Name</Label>
                <Input
                  placeholder="My Awesome Store"
                  value={form.name}
                  onChange={(e) => { const name = e.target.value; setForm((f) => ({ ...f, name, slug: autoSlug(name) })); }}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>URL Slug</Label>
                <div className="flex mt-1">
                  <Input
                    placeholder="my-store"
                    value={form.slug}
                    onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                    className="rounded-r-none"
                  />
                  <span className="flex items-center px-3 border border-l-0 rounded-r-md bg-gray-50 text-gray-500 text-sm whitespace-nowrap">
                    .wenext.app
                  </span>
                </div>
              </div>
              <div>
                <Label>Custom Domain <span className="text-gray-400 font-normal">(optional)</span></Label>
                <Input
                  placeholder="store.yourdomain.com"
                  value={form.domain}
                  onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Description <span className="text-gray-400 font-normal">(optional)</span></Label>
                <Textarea
                  placeholder="What does this store sell?"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  className="mt-1"
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={!form.name || !form.slug || creating}>
                {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create Site
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Website Builder</h1>
          <p className="text-gray-500 mt-1">Create and manage your online stores</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setTemplateModalOpen(true)}
            className="gap-2"
            data-testid="wb-dashboard-new-from-template"
          >
            <Sparkles className="h-4 w-4" />New site from template
          </Button>
          <Button
            onClick={() => setCreateOpen(true)}
            variant="outline"
            className="gap-2"
            data-testid="wb-dashboard-new-blank"
          >
            <Plus className="h-4 w-4" />Blank site
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64" data-testid="wb-dashboard-loading">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : loadError ? (
        <div
          className="flex flex-col items-center justify-center h-64 border-2 border-dashed border-red-200 rounded-2xl text-center bg-red-50/40"
          data-testid="wb-dashboard-load-error"
        >
          <h3 className="text-lg font-semibold text-red-700 mb-1">Could not load sites</h3>
          <p className="text-sm text-red-600 mb-4">{loadError}</p>
          <Button onClick={loadSites} variant="outline" className="gap-2">
            Retry
          </Button>
        </div>
      ) : sites.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 border-2 border-dashed border-gray-200 rounded-2xl text-center">
          <Globe className="h-12 w-12 text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-1">No sites yet</h3>
          <p className="text-gray-400 mb-4">Create your first store to get started</p>
          <Button onClick={() => setTemplateModalOpen(true)} className="gap-2">
            <Sparkles className="h-4 w-4" />New site from template
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sites.map((site) => (
            <div
              key={site.id}
              className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all cursor-pointer group"
              onClick={() => openEditorForSite(site)}
            >
              <div className={`relative h-32 bg-gradient-to-br ${siteGradient(site.name)} flex items-center justify-center overflow-hidden`}>
                <span className="text-8xl font-black text-white/15 select-none leading-none">
                  {site.name[0].toUpperCase()}
                </span>

                <div className="absolute bottom-3 left-3">
                  <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border-0 ${
                    site.is_published
                      ? 'bg-green-500/90 text-white'
                      : 'bg-black/30 text-white/90 backdrop-blur-sm'
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${site.is_published ? 'bg-white' : 'bg-white/60'}`} />
                    {site.is_published ? 'Published' : 'Draft'}
                  </span>
                </div>

                <div className="absolute top-2.5 right-2.5" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 bg-black/25 hover:bg-black/40 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <MoreVertical className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEditorForSite(site)}>
                        <Edit3 className="h-4 w-4 mr-2" />Open Editor
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => setMultiPageTarget(site)}
                        data-testid={`wb-dashboard-generate-pages-${site.id}`}
                      >
                        <Layers className="h-4 w-4 mr-2" />Generate Pages with AI
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleTogglePublish(site)}>
                        {site.is_published
                          ? <EyeOff className="h-4 w-4 mr-2" />
                          : <Eye className="h-4 w-4 mr-2" />}
                        {site.is_published ? 'Unpublish' : 'Publish'}
                      </DropdownMenuItem>
                      {site.domain && (
                        <DropdownMenuItem asChild>
                          <a
                            href={`https://${site.domain}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />View Live
                          </a>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-red-600 focus:text-red-600"
                        onClick={() => setDeleteTarget(site)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />Delete Site
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              <div className="p-4">
                <div className="mb-3">
                  <h3 className="font-semibold text-gray-900 text-base leading-tight truncate">
                    {site.name}
                  </h3>
                  <p className="text-xs text-gray-400 mt-0.5 truncate">
                    {site.slug}.wenext.app
                  </p>
                  {site.description && (
                    <p className="text-xs text-gray-500 mt-1.5 line-clamp-2 leading-relaxed">
                      {site.description}
                    </p>
                  )}
                  {site.updated_at && (
                    <p className="text-[11px] text-gray-400 mt-2 flex items-center gap-1">
                      <Calendar className="h-3 w-3 flex-shrink-0" />
                      Updated {formatDate(site.updated_at)}
                    </p>
                  )}
                </div>

                <div
                  className="flex gap-2 pt-3 border-t border-gray-100"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    size="sm"
                    className="flex-1 h-8 text-xs gap-1.5"
                    onClick={() => openEditorForSite(site)}
                  >
                    <Edit3 className="h-3.5 w-3.5" />Edit Site
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className={`h-8 px-3 text-xs gap-1.5 ${
                      site.is_published
                        ? 'text-orange-600 border-orange-200 hover:bg-orange-50'
                        : 'text-green-600 border-green-200 hover:bg-green-50'
                    }`}
                    onClick={() => handleTogglePublish(site)}
                  >
                    {site.is_published
                      ? <><EyeOff className="h-3.5 w-3.5" />Unpublish</>
                      : <><Eye className="h-3.5 w-3.5" />Publish</>}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Create New Site</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Site Name</Label>
              <Input
                placeholder="My Awesome Store"
                value={form.name}
                onChange={(e) => { const name = e.target.value; setForm((f) => ({ ...f, name, slug: autoSlug(name) })); }}
                className="mt-1"
              />
            </div>
            <div>
              <Label>URL Slug</Label>
              <div className="flex mt-1">
                <Input
                  placeholder="my-store"
                  value={form.slug}
                  onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                  className="rounded-r-none"
                />
                <span className="flex items-center px-3 border border-l-0 rounded-r-md bg-gray-50 text-gray-500 text-sm whitespace-nowrap">
                  .wenext.app
                </span>
              </div>
            </div>
            <div>
              <Label>Custom Domain <span className="text-gray-400 font-normal">(optional)</span></Label>
              <Input
                placeholder="store.yourdomain.com"
                value={form.domain}
                onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Description <span className="text-gray-400 font-normal">(optional)</span></Label>
              <Textarea
                placeholder="What does this store sell?"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!form.name || !form.slug || creating}>
              {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create Site
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the site and all its pages. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteTarget && handleDelete(deleteTarget)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {templateModalOpen && (
        <div
          className="fixed inset-0 z-[60] bg-white overflow-y-auto"
          role="dialog"
          aria-modal="true"
          data-testid="wb-dashboard-template-modal"
        >
          <TemplateGallery
            onComplete={handleTemplateComplete}
            onClose={() => setTemplateModalOpen(false)}
          />
        </div>
      )}

      {multiPageTarget && (
        <MultiPageGenerateModal
          open={!!multiPageTarget}
          onOpenChange={(o) => { if (!o) setMultiPageTarget(null); }}
          siteId={multiPageTarget.id}
          siteName={multiPageTarget.name}
          onComplete={(result) => {
            toast({ variant: "success",
              title: 'Pages generated',
              description: `${result.pageCount} page${result.pageCount === 1 ? '' : 's'} added to "${multiPageTarget.name}".`,
            });
            loadSites();
            if (result.firstPageId) {
              navigate(
                `/home/website-builder/sites/${multiPageTarget.id}/editor/${result.firstPageId}`,
              );
            }
          }}
        />
      )}
    </div>
  );
}
